/**
 * Calls a generated SDK function and returns `data` directly, turning a failed
 * request into an `Error` the views can show.
 *
 * The generated clients are configured with throwOnError, so a non-2xx
 * response rejects -- but with the parsed response *body* (a string for the
 * plain-text errors servant produces, an object for JSON ones), not an Error.
 * `sdk` rethrows it as `Error("<label>: <body>")`, which is what the views'
 * catch blocks display.
 *
 * Usage:
 *   const partners = await sdk('Could not fetch partners', () => getListPartners());
 */

// The generated functions resolve to `{ data: T, request, response }`. `data`
// shows up as `T | undefined` only because TS instantiates the SDK function's
// `ThrowOnError` generic at its constraint (`boolean`) rather than the `true`
// default; with throwOnError the promise never resolves without data.
type SdkData<T> = T extends Promise<{ data: infer D }> ? Exclude<D, undefined> : never;

export const describeApiError = (label: string, error: unknown): Error => {
  if (error instanceof Error) {
    return new Error(`${label}: ${error.message}`);
  }
  const body =
    typeof error === 'string'
      ? error
      : error && typeof error === 'object' && Object.keys(error).length > 0
      ? JSON.stringify(error)
      : 'request failed';
  return new Error(`${label}: ${body}`);
};

export const sdk = async <TResult extends Promise<{ data: unknown }>>(
  label: string,
  call: () => TResult
): Promise<SdkData<TResult>> => {
  try {
    const result = await call();
    return result.data as SdkData<TResult>;
  } catch (error) {
    throw describeApiError(label, error);
  }
};
